import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSnowflake, faLockOpen, faPlus, faUsers, faUser,
  faExclamationTriangle, faTimes, faRotate,
  faFileAlt, faUserTie,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import {
  FreezeEvent, FreezeRecord, FreezeResellerRecord,
  DepositBank, FreezePreviewEvent,
} from '../../types';
import {
  UiPageHeader,
  UiFilterPanel,
  UiModal,
  UiButton,
} from '../../components/ui';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';
const todayBKK = () => dayjs().tz(TZ).format('YYYY-MM-DD');

const INPUT_CLASS =
  'w-full px-3.5 py-2.5 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500';
const TEXTAREA_CLASS = `${INPUT_CLASS} resize-none`;
const LABEL_CLASS =
  'block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5';
const TABLE_ACTION_BTN =
  'px-2.5 py-1.5 text-[11px] rounded-lg border transition-colors';

// ─── Helpers ────────────────────────────────────────────────
const fmt = (amount: number | undefined | null) => {
  if (amount === undefined || amount === null) return '-';
  return new Intl.NumberFormat('th-TH', {
    style: 'currency', currency: 'THB', minimumFractionDigits: 2
  }).format(amount);
};

const fmtDate = (dateStr: string) =>
  new Date(dateStr).toLocaleDateString('th-TH', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });

// ─── Deposit bank filter helpers ────────────────────────────
const bankAccountLabel = (b: Pick<DepositBank, 'bank_code' | 'name' | 'account_number'>) =>
  `[${b.bank_code}] ${b.name} — ${b.account_number}`;

const depositRangeText = (from?: string | null, to?: string | null) => {
  if (from && to) return `${from} ถึง ${to}`;
  if (from) return `ตั้งแต่ ${from}`;
  if (to) return `ถึง ${to}`;
  return 'ทุกช่วงเวลา';
};

// ─── Main Component ──────────────────────────────────────────
const FreezeManagementPage: React.FC = () => {
  // List state (server-side pagination + date filter)
  const [events, setEvents] = useState<FreezeEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [dateFrom, setDateFrom] = useState(todayBKK);
  const [dateTo, setDateTo] = useState(todayBKK);
  const [appliedDateFrom, setAppliedDateFrom] = useState(todayBKK);
  const [appliedDateTo, setAppliedDateTo] = useState(todayBKK);

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<FreezeEvent | null>(null);
  const [records, setRecords] = useState<FreezeRecord[]>([]);
  const [resellerRecords, setResellerRecords] = useState<FreezeResellerRecord[]>([]);
  const [detailLoading, setDetailLoading] = useState(false);

  // Retro freeze-reseller modal state
  const [showFreezeResellerModal, setShowFreezeResellerModal] = useState(false);
  const [freezeResellerPreview, setFreezeResellerPreview] = useState<FreezeResellerRecord[]>([]);
  const [freezeResellerPreviewLoading, setFreezeResellerPreviewLoading] = useState(false);
  const [freezingResellers, setFreezingResellers] = useState(false);

  // Create modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [freezeAmount, setFreezeAmount] = useState('');
  const [freezeDate, setFreezeDate] = useState(todayBKK());
  const [freezeNotes, setFreezeNotes] = useState('');
  const [creating, setCreating] = useState(false);
  const [freezeType, setFreezeType] = useState<'all' | 'single'>('all');
  const [merchantSearch, setMerchantSearch] = useState('');
  const [merchantOptions, setMerchantOptions] = useState<{ id: string; name: string; email: string }[]>([]);
  const [merchantOptionsLoading, setMerchantOptionsLoading] = useState(false);
  const [selectedMerchant, setSelectedMerchant] = useState<{ id: string; name: string; email: string } | null>(null);
  const [showMerchantDropdown, setShowMerchantDropdown] = useState(false);

  // Deposit bank filter (ไม่บังคับ — เฉพาะโหมด "ทั้งหมด")
  const [depositBanks, setDepositBanks] = useState<DepositBank[]>([]);
  const [depositBanksLoaded, setDepositBanksLoaded] = useState(false);
  const [bankFilterSupported, setBankFilterSupported] = useState(false);
  const [depositBankId, setDepositBankId] = useState('');
  const [depositFrom, setDepositFrom] = useState('');
  const [depositTo, setDepositTo] = useState('');

  // Freeze confirm modal state
  const [showFreezeConfirmModal, setShowFreezeConfirmModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewEvent, setPreviewEvent] = useState<FreezePreviewEvent | null>(null);
  const [previewRecords, setPreviewRecords] = useState<FreezeRecord[]>([]);
  const [previewResellerRecords, setPreviewResellerRecords] = useState<FreezeResellerRecord[]>([]);
  // พารามิเตอร์ที่ใช้ preview จริง — ยืนยันต้องส่งชุดเดียวกันเป๊ะ (ฟอร์มยังแก้ได้ระหว่างรอ preview)
  const [previewScope, setPreviewScope] = useState<Record<string, string> | null>(null);

  // Unfreeze modal state
  const [showUnfreezeModal, setShowUnfreezeModal] = useState(false);
  const [unfreezeStep, setUnfreezeStep] = useState<1 | 2>(1);
  const [feeAmount, setFeeAmount] = useState('');
  const [unfreezing, setUnfreezing] = useState(false);
  const [returnMethod, setReturnMethod] = useState<'main_wallet' | 'usdt_settlement'>('main_wallet');
  const [settleRate, setSettleRate] = useState<number | null>(null);
  const [usdtRate, setUsdtRate] = useState<number | null>(null);
  const [usdtRateLoading, setUsdtRateLoading] = useState(false);

  // ─── Load events (server-side pagination + date filter) ──
  const loadEvents = useCallback(async (page = currentPage, size = pageSize) => {
    try {
      setLoading(true);
      const offset = (page - 1) * size;
      const params = new URLSearchParams({
        limit:  String(size),
        offset: String(offset),
        ...(appliedDateFrom && { start_date: appliedDateFrom }),
        ...(appliedDateTo   && { end_date:   appliedDateTo }),
      });
      const res = await merchantApi.get(`/admin/freeze?${params}`);
      setEvents(res.data.data || []);
      setTotalItems(res.data.total || 0);
    } catch {
      toast.error('ไม่สามารถโหลดข้อมูล Freeze ได้');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, appliedDateFrom, appliedDateTo]);

  useEffect(() => { loadEvents(currentPage, pageSize); }, [currentPage, pageSize, appliedDateFrom, appliedDateTo]); // eslint-disable-line

  const handleSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
      return;
    }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setCurrentPage(1);
  };

  const handleReset = () => {
    const today = todayBKK();
    setDateFrom(today);
    setDateTo(today);
    setAppliedDateFrom(today);
    setAppliedDateTo(today);
    setCurrentPage(1);
  };

  // ─── Open detail modal ───────────────────────────────────
  const openDetail = async (event: FreezeEvent) => {
    setSelectedEvent(event);
    setShowDetailModal(true);
    setRecords([]);
    setResellerRecords([]);
    setDetailLoading(true);
    try {
      const res = await merchantApi.get(`/admin/freeze/${event.id}`);
      setSelectedEvent(res.data.data.event);
      setRecords(res.data.data.records || []);
      setResellerRecords(res.data.data.reseller_records || []);
      setSettleRate(res.data.data.settlement_fee_rate ?? null);
    } catch {
      toast.error('ไม่สามารถโหลดรายละเอียดได้');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setShowDetailModal(false);
    setSelectedEvent(null);
    setRecords([]);
    setResellerRecords([]);
  };

  // โหลดรายการบัญชีฝากสำหรับตัวกรอง — backend เก่า/tenant ที่ยังไม่ migrate = ซ่อน UI เงียบ ๆ (ไม่ toast)
  const loadDepositBanks = async () => {
    if (depositBanksLoaded) return;
    try {
      const res = await merchantApi.get('/admin/freeze/deposit-banks');
      const data = res.data?.data;
      const supported = !!data?.supported;
      const banks: DepositBank[] = Array.isArray(data?.banks) ? data.banks : [];
      // บัญชีที่ใช้งานอยู่ขึ้นก่อน แล้วตามด้วยบัญชีที่ปิดใช้งาน (ลำดับเดิมภายในกลุ่ม)
      const sorted = [...banks.filter(b => b.is_active), ...banks.filter(b => !b.is_active)];
      setDepositBanks(sorted);
      setBankFilterSupported(supported);
      setDepositBanksLoaded(true);
    } catch {
      setBankFilterSupported(false);
    }
  };

  const handleDepositBankChange = (bankId: string) => {
    setDepositBankId(bankId);
    if (!bankId) {
      setDepositFrom('');
      setDepositTo('');
    }
  };

  // ส่งตัวกรองบัญชีฝากเฉพาะโหมด "ทั้งหมด" ที่เลือกบัญชีแล้ว — ห้ามส่งคู่กับ merchant_id (backend ตอบ 400)
  const bankFilterActive = freezeType === 'all' && bankFilterSupported && !!depositBankId;
  const selectedDepositBank = bankFilterActive
    ? depositBanks.find(b => b.id === depositBankId) || null
    : null;
  const bankFilterParams = (): Record<string, string> => {
    if (!bankFilterActive) return {};
    return {
      bank_id: depositBankId,
      ...(depositFrom ? { deposit_from: depositFrom } : {}),
      ...(depositTo ? { deposit_to: depositTo } : {}),
    };
  };

  const openCreateModal = () => {
    setFreezeAmount('');
    setFreezeDate(todayBKK());
    setFreezeNotes('');
    setFreezeType('all');
    setMerchantSearch('');
    setSelectedMerchant(null);
    setShowMerchantDropdown(false);
    setPreviewResellerRecords([]);
    setPreviewScope(null);
    setDepositBankId('');
    setDepositFrom('');
    setDepositTo('');
    setShowCreateModal(true);
    loadDepositBanks();
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
  };

  const closeUnfreezeModal = () => {
    if (unfreezing) return;
    setShowUnfreezeModal(false);
    setFeeAmount('');
    setUnfreezeStep(1);
    setReturnMethod('main_wallet');
    setUsdtRate(null);
  };

  const loadUsdtRate = async () => {
    if (usdtRate !== null || usdtRateLoading) return;
    setUsdtRateLoading(true);
    try {
      const rateRes = await merchantApi.get('/admin/freeze/usdt-rate');
      setUsdtRate(rateRes.data.data?.rate || null);
    } catch { setUsdtRate(null); }
    finally { setUsdtRateLoading(false); }
  };

  const loadMerchants = async () => {
    if (merchantOptions.length > 0) return;
    try {
      setMerchantOptionsLoading(true);
      const res = await merchantApi.get('/merchants');
      setMerchantOptions((res.data.data || []).map((m: any) => ({ id: m.id, name: m.name, email: m.email })));
    } catch {
      toast.error('ไม่สามารถโหลดรายการ Merchant ได้');
    } finally {
      setMerchantOptionsLoading(false);
    }
  };

  const filteredMerchants = merchantOptions.filter(m =>
    m.name.toLowerCase().includes(merchantSearch.toLowerCase()) ||
    m.email.toLowerCase().includes(merchantSearch.toLowerCase())
  );

  // ─── Create freeze ───────────────────────────────────────
  const handleCreate = async () => {
    if (!freezeDate) { toast.error('กรุณาระบุวันที่ Freeze'); return; }
    // ยอด / ร้าน / บัญชีฝาก มาจากชุดที่ preview ไว้ (ตรงกับที่ modal ยืนยันแสดง) ไม่ใช่ค่าในฟอร์มตอนนี้
    if (!previewScope) { toast.error('กรุณากด Freeze เพื่อดู Preview ก่อน'); return; }
    const { freeze_amount: previewAmount, ...scopeParams } = previewScope;
    const amount = parseFloat(previewAmount);
    if (isNaN(amount) || amount <= 0) { toast.error('กรุณาระบุจำนวนเงินที่ต้องการ Freeze'); return; }
    try {
      setCreating(true);
      const res = await merchantApi.post('/admin/freeze', {
        freeze_date:   freezeDate,
        freeze_amount: amount,
        notes:         freezeNotes || undefined,
        ...scopeParams,
      });
      toast.success(res.data.message || 'Freeze สำเร็จ');
      closeCreateModal();
      setCurrentPage(1);
      await loadEvents(1, pageSize);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'เกิดข้อผิดพลาดในการ Freeze');
    } finally {
      setCreating(false);
    }
  };

  // ─── Unfreeze ────────────────────────────────────────────
  const handleUnfreezeStep1 = () => {
    const fee = parseFloat(feeAmount);
    if (isNaN(fee) || fee < 0) { toast.error('กรุณาระบุค่าดำเนินการ (0 ขึ้นไป)'); return; }
    if (selectedEvent && fee > selectedEvent.total_frozen) { toast.error('ค่าดำเนินการเกินกว่ายอดที่ถูก Freeze'); return; }
    setUnfreezeStep(2);
  };

  const handleUnfreeze = async () => {
    if (!selectedEvent) return;
    const fee = parseFloat(feeAmount);
    try {
      setUnfreezing(true);
      const res = await merchantApi.post(`/admin/freeze/${selectedEvent.id}/unfreeze`, {
        fee_amount:    fee,
        return_method: returnMethod,
      });
      toast.success(res.data.message || 'Unfreeze สำเร็จ');
      setShowUnfreezeModal(false);
      setFeeAmount('');
      setUnfreezeStep(1);
      setReturnMethod('main_wallet');
      setUsdtRate(null);
      // Refresh detail
      const detail = await merchantApi.get(`/admin/freeze/${selectedEvent.id}`);
      setSelectedEvent(detail.data.data.event);
      setRecords(detail.data.data.records || []);
      setResellerRecords(detail.data.data.reseller_records || []);
      setSettleRate(detail.data.data.settlement_fee_rate ?? null);
      await loadEvents(currentPage, pageSize);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'เกิดข้อผิดพลาดในการ Unfreeze');
    } finally {
      setUnfreezing(false);
    }
  };

  // ─── Freeze Reseller ย้อนหลัง ────────────────────────────
  const openFreezeResellerModal = async () => {
    if (!selectedEvent) return;
    setShowFreezeResellerModal(true);
    setFreezeResellerPreview([]);
    setFreezeResellerPreviewLoading(true);
    try {
      const res = await merchantApi.get(`/admin/freeze/preview?event_id=${selectedEvent.id}`);
      setFreezeResellerPreview(res.data.data.reseller_records || []);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'ไม่สามารถโหลด Preview Reseller ได้');
      setShowFreezeResellerModal(false);
    } finally {
      setFreezeResellerPreviewLoading(false);
    }
  };

  const handleFreezeResellers = async () => {
    if (!selectedEvent) return;
    try {
      setFreezingResellers(true);
      const res = await merchantApi.post(`/admin/freeze/${selectedEvent.id}/freeze-resellers`);
      toast.success(res.data.message || `Freeze Reseller สำเร็จ ${res.data.data?.reseller_count ?? ''} ราย`);
      setShowFreezeResellerModal(false);
      const detail = await merchantApi.get(`/admin/freeze/${selectedEvent.id}`);
      setSelectedEvent(detail.data.data.event);
      setRecords(detail.data.data.records || []);
      setResellerRecords(detail.data.data.reseller_records || []);
      setSettleRate(detail.data.data.settlement_fee_rate ?? null);
      await loadEvents(currentPage, pageSize);
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'เกิดข้อผิดพลาดในการ Freeze Reseller');
    } finally {
      setFreezingResellers(false);
    }
  };

  // ─── Computed summary ────────────────────────────────────
  const totalPayin    = records.reduce((s, r) => s + Number(r.payin_amount), 0);
  const totalPayout   = records.reduce((s, r) => s + Number(r.payout_amount), 0);
  const totalFrozen   = records.reduce((s, r) => s + Number(r.total_frozen), 0);
  const totalReturned = records.reduce((s, r) => s + Number(r.returned_amount || 0), 0);
  const totalDeducted = records.reduce((s, r) => s + Number(r.deducted_amount || 0), 0);
  const isUnfrozen    = selectedEvent?.status === 'unfrozen';
  // event ที่ Freeze แบบกรองบัญชีฝาก — reseller ใช้สูตร spread (ต้องเช็คก่อน merchant_count === 1)
  const selectedIsBankEvent = !!(selectedEvent?.bank_filter || selectedEvent?.bank_id);
  const feeNum        = parseFloat(feeAmount) || 0;
  // fee % คิดจากยอด freeze ของ merchant เท่านั้น (reseller ใช้ % เดียวกัน ไม่รวมเข้า pool)
  const feePct        = selectedEvent && selectedEvent.total_frozen > 0 ? feeNum / selectedEvent.total_frozen * 100 : 0;
  const previewReturn = selectedEvent ? selectedEvent.total_frozen - feeNum : 0;
  const previewDeduct = feeNum;
  // ─── Return method (unfreeze v5) ─────────────────────────
  const isMainWallet = returnMethod === 'main_wallet';
  // เรทค่าธรรมเนียม settlement: event ที่ unfrozen แล้วใช้ค่า snapshot จาก event, ระหว่าง preview ใช้ค่าปัจจุบันจาก system_settings
  const snapshotRate     = isUnfrozen && selectedEvent?.settlement_fee_rate != null ? Number(selectedEvent.settlement_fee_rate) : null;
  const rateNum          = snapshotRate ?? settleRate ?? 5;
  const previewSettleFee = parseFloat((previewReturn * rateNum / 100).toFixed(2));
  const previewNet       = isMainWallet ? previewReturn : parseFloat((previewReturn - previewSettleFee).toFixed(2));

  // ─── Reseller computed ───────────────────────────────────
  const totalResellerFrozen    = resellerRecords.reduce((s, r) => s + Number(r.frozen_amount), 0);
  const totalResellerReturned  = resellerRecords.reduce((s, r) => s + Number(r.returned_amount || 0), 0);
  const totalResellerDeducted  = resellerRecords.reduce((s, r) => s + Number(r.deducted_amount || 0), 0);
  const resellerDeductPreview  = parseFloat((totalResellerFrozen * feePct / 100).toFixed(2));
  const resellerReturnPreview  = parseFloat((totalResellerFrozen - resellerDeductPreview).toFixed(2));
  const freezeResellerPreviewTotal = freezeResellerPreview.reduce((s, r) => s + Number(r.frozen_amount), 0);

  const listColumns: Column<FreezeEvent>[] = useMemo(() => [
    {
      key: 'freeze_date',
      header: 'วันที่ Freeze',
      render: (e) => <span className="font-semibold text-gray-800 dark:text-slate-100">{e.freeze_date}</span>,
    },
    {
      key: 'status',
      header: 'สถานะ',
      render: (e) => (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
          e.status === 'frozen'
            ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
            : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
        }`}>
          <FontAwesomeIcon icon={e.status === 'frozen' ? faSnowflake : faLockOpen} />
          {e.status === 'frozen' ? 'Frozen' : 'Unfrozen'}
        </span>
      ),
    },
    {
      key: 'merchant_count',
      header: 'Merchants',
      render: (e) => (
        <div>
          <span className="flex items-center gap-1.5 text-gray-600 dark:text-slate-300">
            <FontAwesomeIcon icon={faUsers} className="text-gray-400 dark:text-slate-500 text-xs" />
            {e.merchant_count}
          </span>
          {(e.reseller_count || 0) > 0 && (
            <span className="text-[10px] text-purple-500 dark:text-purple-400">
              +{e.reseller_count} reseller
            </span>
          )}
        </div>
      ),
    },
    {
      key: 'bank_filter',
      header: 'บัญชีฝาก',
      render: (e) => e.bank_filter
        ? (
          <span
            className="text-xs text-gray-700 dark:text-slate-300 whitespace-nowrap"
            title={`${bankAccountLabel(e.bank_filter)} • ${depositRangeText(e.bank_filter.deposit_from, e.bank_filter.deposit_to)}`}
          >
            [{e.bank_filter.bank_code}] {e.bank_filter.name}
          </span>
        )
        : <span className="text-gray-400 dark:text-slate-500">-</span>,
    },
    {
      key: 'freeze_amount_requested',
      header: 'ยอดที่ Freeze',
      render: (e) => <span className="font-bold text-red-600 dark:text-red-400">{fmt(e.freeze_amount_requested ?? e.total_frozen)}</span>,
    },
    {
      key: 'total_frozen',
      header: 'จากยอด Pool',
      render: (e) => {
        if (e.freeze_amount_requested != null && e.freeze_percentage != null && e.freeze_percentage > 0) {
          return <span className="text-gray-700 dark:text-slate-300">{fmt(e.freeze_amount_requested / e.freeze_percentage * 100)}</span>;
        }
        return <span className="text-gray-400 dark:text-slate-500">-</span>;
      },
    },
    {
      key: 'freeze_percentage',
      header: 'หัก %',
      render: (e) => e.freeze_percentage != null
        ? <span className="font-semibold text-orange-600 dark:text-orange-400">{Number(e.freeze_percentage).toFixed(4)}%</span>
        : '-',
    },
    {
      key: 'created_at',
      header: 'สร้างเมื่อ',
      render: (e) => <span className="text-gray-500 dark:text-slate-400 text-xs">{fmtDate(e.created_at)}</span>,
    },
    {
      key: 'id',
      header: '',
      render: (e) => (
        <button
          type="button"
          onClick={(ev) => { ev.stopPropagation(); openDetail(e); }}
          className={`${TABLE_ACTION_BTN} bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 text-blue-600 dark:text-blue-400 border-blue-200 dark:border-blue-800`}
        >
          <FontAwesomeIcon icon={faFileAlt} className="mr-1" />
          รายละเอียด
        </button>
      ),
    },
  ], []);

  const recordColumns: Column<FreezeRecord>[] = useMemo(() => [
    {
      key: 'merchant_name',
      header: 'Merchant',
      render: (r) => (
        <div>
          <div className="font-medium text-gray-800 dark:text-slate-100">{r.merchant_name}</div>
          <div className="text-xs text-gray-400 dark:text-slate-500">{r.merchant_email}</div>
        </div>
      ),
    },
    {
      key: 'payin_balance_before',
      header: 'Pay-in ก่อนหัก',
      render: (r) => <span className="text-gray-600 dark:text-slate-300">{fmt(r.payin_balance_before)}</span>,
    },
    {
      key: 'payout_balance_before',
      header: 'Pay-out ก่อนหัก',
      render: (r) => <span className="text-gray-600 dark:text-slate-300">{fmt(r.payout_balance_before)}</span>,
    },
    {
      key: 'total_frozen',
      header: 'ยอดที่หัก',
      render: (r) => <span className="font-bold text-red-600 dark:text-red-400">-{fmt(r.total_frozen)}</span>,
    },
    {
      key: 'payin_amount',
      header: 'คงเหลือ',
      render: (r) => {
        const before = (r.payin_balance_before || 0) + (r.payout_balance_before || 0);
        const remaining = before - (r.total_frozen || 0);
        return <span className="font-semibold text-blue-600 dark:text-blue-400">{fmt(remaining)}</span>;
      },
    },
    ...(isUnfrozen ? [
      {
        key: 'returned_amount' as keyof FreezeRecord,
        header: 'คืนแล้ว',
        render: (r: FreezeRecord) => <span className="text-green-600 dark:text-green-400 font-medium">{fmt(r.returned_amount)}</span>,
      },
      {
        key: 'deducted_amount' as keyof FreezeRecord,
        header: 'ค่าดำเนินการ',
        render: (r: FreezeRecord) => <span className="text-orange-500 dark:text-orange-400">{fmt(r.deducted_amount)}</span>,
      },
    ] : []),
  ], [isUnfrozen]);

  const resellerColumns: Column<FreezeResellerRecord>[] = useMemo(() => [
    {
      key: 'reseller_name',
      header: 'Reseller',
      render: (r) => (
        <div>
          <div className="font-medium text-gray-800 dark:text-slate-100">{r.reseller_name}</div>
          <div className="text-xs text-gray-400 dark:text-slate-500">{r.reseller_email}</div>
        </div>
      ),
    },
    {
      key: 'balance_before',
      header: 'Balance ก่อนหัก',
      render: (r) => <span className="text-gray-600 dark:text-slate-300">{fmt(r.balance_before)}</span>,
    },
    {
      key: 'frozen_amount',
      header: 'ยอดที่หัก',
      render: (r) => <span className="font-bold text-red-600 dark:text-red-400">-{fmt(r.frozen_amount)}</span>,
    },
    {
      key: 'created_at',
      header: 'คงเหลือ',
      render: (r) => (
        <span className="font-semibold text-blue-600 dark:text-blue-400">
          {fmt((r.balance_before || 0) - (r.frozen_amount || 0))}
        </span>
      ),
    },
    ...(isUnfrozen ? [
      {
        key: 'returned_amount' as keyof FreezeResellerRecord,
        header: 'คืนแล้ว',
        render: (r: FreezeResellerRecord) => <span className="text-green-600 dark:text-green-400 font-medium">{fmt(r.returned_amount)}</span>,
      },
      {
        key: 'deducted_amount' as keyof FreezeResellerRecord,
        header: 'ค่าดำเนินการ',
        render: (r: FreezeResellerRecord) => <span className="text-orange-500 dark:text-orange-400">{fmt(r.deducted_amount)}</span>,
      },
    ] : []),
  ], [isUnfrozen]);

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faSnowflake}
        title="Freeze Wallet"
        subtitle={`จัดการการ Freeze เงิน Merchant • ${totalItems.toLocaleString()} รายการ`}
        actions={
          <UiButton icon={faPlus} onClick={openCreateModal}>
            สร้าง Freeze
          </UiButton>
        }
      />

      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        actions={
          <button
            type="button"
            onClick={() => loadEvents(currentPage, pageSize)}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium border border-gray-200 hover:bg-gray-50 transition-colors text-gray-600 dark:border-slate-600 dark:hover:bg-slate-700 dark:text-slate-300 flex items-center gap-2 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        }
      />

      <DataTable
        data={events}
        columns={listColumns}
        loading={loading}
        onRowClick={(e) => openDetail(e)}
        hoverable
        compact
        emptyMessage="ยังไม่มีรายการ"
        emptyIcon={<FontAwesomeIcon icon={faSnowflake} className="text-5xl text-gray-200 dark:text-slate-600" />}
        serverPagination={{
          currentPage,
          totalItems,
          pageSize,
          onPageChange: (page) => setCurrentPage(page),
          onPageSizeChange: (size) => { setPageSize(size); setCurrentPage(1); },
        }}
      />

      <UiModal
        open={showDetailModal && !!selectedEvent}
        onClose={closeDetail}
        title={selectedEvent ? `รายละเอียด Freeze — ${selectedEvent.freeze_date}` : 'รายละเอียด Freeze'}
        icon={faSnowflake}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="xl"
        scrollable
        footer={
          selectedEvent?.status === 'frozen' ? (
            <UiButton
              icon={faLockOpen}
              onClick={() => {
                setReturnMethod('main_wallet');
                setShowUnfreezeModal(true);
              }}
              className="w-full justify-center !bg-green-600 hover:!bg-green-700"
            >
              ปลด Freeze
            </UiButton>
          ) : undefined
        }
      >
        {selectedEvent && (
          <>
            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
              selectedEvent.status === 'frozen'
                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
            }`}>
              <FontAwesomeIcon icon={selectedEvent.status === 'frozen' ? faSnowflake : faLockOpen} />
              {selectedEvent.status === 'frozen' ? 'Frozen' : 'Unfrozen'}
            </span>

            <div className="space-y-4 -mt-2">
            {selectedEvent.freeze_amount_requested != null && selectedEvent.freeze_percentage != null && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800 rounded-xl p-4 flex flex-wrap gap-6 items-center">
                <div>
                  <p className="text-xs text-red-400 dark:text-red-400 mb-0.5">ยอดที่ขอ Freeze</p>
                  <p className="text-xl font-bold text-red-700 dark:text-red-400">{fmt(selectedEvent.freeze_amount_requested)}</p>
                </div>
                <div className="text-gray-300 dark:text-slate-600 text-xl font-light hidden sm:block">จาก</div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mb-0.5">
                    {selectedIsBankEvent ? 'ยอด Pool ของร้านที่ฝากผ่านบัญชีนี้' : 'ยอด Pool รวมทั้งหมด'}
                  </p>
                  <p className="text-xl font-bold text-gray-800 dark:text-slate-100">
                    {selectedEvent.freeze_percentage > 0
                      ? fmt(selectedEvent.freeze_amount_requested / selectedEvent.freeze_percentage * 100)
                      : fmt((selectedEvent.total_payin ?? 0) + (selectedEvent.total_payout ?? 0))}
                  </p>
                </div>
                <div className="text-gray-300 dark:text-slate-600 text-xl font-light hidden sm:block">=</div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-slate-500 mb-0.5">หัก</p>
                  <p className="text-xl font-bold text-orange-600 dark:text-orange-400">{Number(selectedEvent.freeze_percentage).toFixed(4)}%</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-xl p-4" style={{ background: 'var(--theme-primary-light)' }}>
                <p className="text-xs font-medium mb-1" style={{ color: 'var(--theme-primary)' }}>Freeze Wallet</p>
                <p className="text-xl font-bold text-red-600 dark:text-red-400">{fmt(selectedEvent.freeze_wallet_balance || selectedEvent.total_frozen)}</p>
              </div>
              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4">
                <p className="text-xs text-gray-400 dark:text-slate-500 mb-1">Merchants</p>
                <p className="text-lg font-bold text-gray-800 dark:text-slate-100">{selectedEvent.merchant_count}</p>
              </div>
            </div>

            {isUnfrozen && (
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border border-green-100 dark:border-green-800">
                  <p className="text-xs text-green-600 dark:text-green-400 mb-1">คืนให้ Merchant รวม ({(100 - (selectedEvent.deduction_percentage || 0)).toFixed(4)}%)</p>
                  <p className="text-xl font-bold text-green-700 dark:text-green-400">{fmt(selectedEvent.total_returned)}</p>
                  <p className="text-[10px] text-green-600/80 dark:text-green-500 mt-1">
                    {selectedEvent.return_method === 'main_wallet'
                      ? 'คืนเข้า Main Wallet (ไม่หักค่าธรรมเนียม)'
                      : `USDT Settlement (ค่าธรรมเนียม ${selectedEvent.settlement_fee_rate ?? 5}%)`}
                  </p>
                </div>
                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4 border border-orange-100 dark:border-orange-800">
                  <p className="text-xs text-orange-600 dark:text-orange-400 mb-1">ค่าดำเนินการ ({Number(selectedEvent.deduction_percentage || 0).toFixed(4)}%)</p>
                  <p className="text-xl font-bold text-orange-700 dark:text-orange-400">{fmt(selectedEvent.fee_amount)}</p>
                </div>
              </div>
            )}

            {selectedEvent.bank_filter && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 text-sm text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800 space-y-0.5">
                <p><span className="font-medium">บัญชีฝาก: </span>{bankAccountLabel(selectedEvent.bank_filter)}</p>
                <p><span className="font-medium">ช่วงวันที่ฝาก: </span>{depositRangeText(selectedEvent.bank_filter.deposit_from, selectedEvent.bank_filter.deposit_to)}</p>
                <p><span className="font-medium">เคยฝาก: </span>{selectedEvent.bank_filter.deposit_merchant_count} ร้าน</p>
              </div>
            )}

            {selectedEvent.notes && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 text-sm text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800">
                <span className="font-medium">หมายเหตุ: </span>{selectedEvent.notes}
              </div>
            )}

            <div className="border border-gray-100 dark:border-slate-700 rounded-xl overflow-hidden">
              <DataTable
                data={records}
                columns={recordColumns}
                loading={detailLoading}
                hoverable
                compact
                emptyMessage="ไม่มีข้อมูล Merchant"
              />
              {records.length > 0 && (
                <div className="border-t-2 border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 px-4 py-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div>
                    <p className="text-xs text-gray-400 dark:text-slate-500">รวม Pay-in</p>
                    <p className="font-bold text-gray-800 dark:text-slate-100">{fmt(totalPayin)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-slate-500">รวม Pay-out</p>
                    <p className="font-bold text-gray-800 dark:text-slate-100">{fmt(totalPayout)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400 dark:text-slate-500">รวม Frozen ทั้งหมด</p>
                    <p className="font-bold text-red-600 dark:text-red-400 text-base">{fmt(totalFrozen)}</p>
                  </div>
                  {isUnfrozen && (
                    <div>
                      <p className="text-xs text-gray-400 dark:text-slate-500">คืน / หัก</p>
                      <p className="font-medium">
                        <span className="text-green-600 dark:text-green-400">{fmt(totalReturned)}</span>
                        <span className="text-gray-300 dark:text-slate-600 mx-1">/</span>
                        <span className="text-orange-500 dark:text-orange-400">{fmt(totalDeducted)}</span>
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ─── Reseller section ─── */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">
                  <FontAwesomeIcon icon={faUserTie} className="mr-1.5" />
                  Reseller ที่ถูก Freeze {resellerRecords.length > 0 && `(${resellerRecords.length} ราย)`}
                </p>
                {selectedEvent.status === 'frozen' && !selectedEvent.reseller_frozen_at && resellerRecords.length === 0 && !detailLoading && (
                  <button
                    type="button"
                    onClick={openFreezeResellerModal}
                    className={`${TABLE_ACTION_BTN} bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/50 text-purple-600 dark:text-purple-400 border-purple-200 dark:border-purple-800`}
                  >
                    <FontAwesomeIcon icon={faSnowflake} className="mr-1" />
                    Freeze Reseller
                  </button>
                )}
              </div>
              {resellerRecords.length > 0 ? (
                <div className="border border-gray-100 dark:border-slate-700 rounded-xl overflow-hidden">
                  <DataTable
                    data={resellerRecords}
                    columns={resellerColumns}
                    loading={detailLoading}
                    hoverable
                    compact
                    emptyMessage="ไม่มีข้อมูล Reseller"
                  />
                  <div className="border-t-2 border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/50 px-4 py-3 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                    <div>
                      <p className="text-xs text-gray-400 dark:text-slate-500">รวม Balance ก่อนหัก</p>
                      <p className="font-bold text-gray-800 dark:text-slate-100">{fmt(resellerRecords.reduce((s, r) => s + Number(r.balance_before || 0), 0))}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 dark:text-slate-500">รวม Frozen Reseller</p>
                      <p className="font-bold text-red-600 dark:text-red-400 text-base">{fmt(totalResellerFrozen)}</p>
                    </div>
                    {isUnfrozen && (
                      <div>
                        <p className="text-xs text-gray-400 dark:text-slate-500">คืน / หัก</p>
                        <p className="font-medium">
                          <span className="text-green-600 dark:text-green-400">{fmt(totalResellerReturned)}</span>
                          <span className="text-gray-300 dark:text-slate-600 mx-1">/</span>
                          <span className="text-orange-500 dark:text-orange-400">{fmt(totalResellerDeducted)}</span>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : selectedEvent.reseller_frozen_at ? (
                <p className="text-xs text-gray-400 dark:text-slate-500">
                  <FontAwesomeIcon icon={faLockOpen} className="mr-1 text-green-400" />
                  Freeze Reseller แล้ว — ไม่มี Reseller ที่เข้าเกณฑ์ (ร้านไม่มี Reseller, ไม่มี Commission จากร้านนี้ หรือยอดคำนวณได้ ฿0)
                </p>
              ) : (
                <p className="text-xs text-gray-400 dark:text-slate-500">ยังไม่มีการ Freeze Reseller สำหรับรายการนี้</p>
              )}
            </div>
            </div>
          </>
        )}
      </UiModal>

      <UiModal
        open={showFreezeResellerModal && !!selectedEvent}
        onClose={() => !freezingResellers && setShowFreezeResellerModal(false)}
        title="Freeze Reseller ย้อนหลัง"
        icon={faUserTie}
        iconColor="text-purple-500"
        size="lg"
        scrollable
        footer={
          selectedEvent ? (
            <div className="flex gap-3">
              <UiButton variant="secondary" onClick={() => setShowFreezeResellerModal(false)} disabled={freezingResellers} className="flex-1 justify-center">
                ยกเลิก
              </UiButton>
              <UiButton
                variant="danger"
                icon={faSnowflake}
                onClick={handleFreezeResellers}
                disabled={freezingResellers || freezeResellerPreviewLoading}
                loading={freezingResellers}
                className="flex-1 justify-center"
              >
                {freezingResellers
                  ? 'กำลัง Freeze...'
                  : freezeResellerPreview.length === 0
                    ? 'ยืนยัน (ปิดการ Freeze Reseller)'
                    : 'ยืนยัน Freeze Reseller'}
              </UiButton>
            </div>
          ) : undefined
        }
      >
        {selectedEvent && (
          <>
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mt-0.5 shrink-0" />
              <div className="text-sm text-red-700 dark:text-red-400">
                <p className="font-semibold mb-1">การดำเนินการนี้ไม่สามารถยกเลิกได้</p>
                <p>
                  {selectedIsBankEvent
                    ? 'ระบบจะหัก Reseller ตามส่วนต่างค่าธรรมเนียม (spread) ของร้านที่ถูก Freeze ในรายการนี้ (ไม่เกินยอดคงเหลือ)'
                    : selectedEvent.merchant_count === 1
                      ? `ระบบจะหัก ${Number(selectedEvent.freeze_percentage || 0).toFixed(4)}% จากยอด Commission ที่ Reseller ได้รับจากร้านที่ถูก Freeze (ไม่เกินยอดคงเหลือ)`
                      : `ระบบจะหัก ${Number(selectedEvent.freeze_percentage || 0).toFixed(4)}% จากยอดคงเหลือปัจจุบันของ Reseller ทุกราย`}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl p-3 text-center bg-gray-50 dark:bg-slate-700/50">
                <p className="text-xs text-gray-400 dark:text-slate-500 mb-1">หัก %</p>
                <p className="text-xl font-bold text-gray-800 dark:text-slate-100">{Number(selectedEvent.freeze_percentage || 0).toFixed(4)}%</p>
              </div>
              <div className="rounded-xl p-3 text-center bg-gray-50 dark:bg-slate-700/50">
                <p className="text-xs text-gray-400 dark:text-slate-500 mb-1">Resellers</p>
                <p className="text-xl font-bold text-gray-800 dark:text-slate-100">{freezeResellerPreview.length} ราย</p>
              </div>
              <div className="rounded-xl p-3 text-center border border-red-100 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
                <p className="text-xs text-gray-400 dark:text-slate-500 mb-1">รวมที่จะหัก</p>
                <p className="text-xl font-bold text-red-600 dark:text-red-400">{fmt(freezeResellerPreviewTotal)}</p>
              </div>
            </div>

            {freezeResellerPreviewLoading ? (
              <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-4">กำลังคำนวณ...</p>
            ) : freezeResellerPreview.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-4">
                ไม่มี Reseller ที่เข้าเกณฑ์ให้หักสำหรับรายการนี้
                {selectedIsBankEvent
                  ? ' (ร้านที่ถูก Freeze ไม่มี Reseller หรือไม่มีส่วนต่างค่าธรรมเนียมจากร้านเหล่านี้)'
                  : selectedEvent.merchant_count === 1 && ' (ร้านไม่มี Reseller หรือไม่มี Commission จากร้านนี้)'}
                <br />กดยืนยันเพื่อปิดการ Freeze Reseller ของรายการนี้ — ปุ่มจะไม่แสดงอีก
              </p>
            ) : (
              <div className="rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                <div className="grid grid-cols-9 gap-2 px-3 py-2 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-100 dark:border-slate-700 text-xs font-semibold text-gray-500 dark:text-slate-400">
                  <div className="col-span-3">Reseller</div>
                  <div className="col-span-2 text-right">Balance ปัจจุบัน</div>
                  <div className="col-span-2 text-right text-orange-500 dark:text-orange-400">หักออก</div>
                  <div className="col-span-2 text-right text-blue-500 dark:text-blue-400">คงเหลือ</div>
                </div>
                <div className="divide-y divide-gray-50 dark:divide-slate-700 max-h-60 overflow-y-auto">
                  {freezeResellerPreview.map((r) => (
                    <div key={r.id} className="grid grid-cols-9 gap-2 px-3 py-2.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                      <div className="col-span-3">
                        <p className="font-medium text-gray-800 dark:text-slate-100 truncate">{r.reseller_name}</p>
                        <p className="text-gray-400 dark:text-slate-500 truncate text-[10px]">{r.reseller_email}</p>
                      </div>
                      <div className="col-span-2 text-right text-gray-600 dark:text-slate-300 self-center">{fmt(r.balance_before)}</div>
                      <div className="col-span-2 text-right text-orange-500 dark:text-orange-400 font-bold self-center">-{fmt(r.frozen_amount)}</div>
                      <div className="col-span-2 text-right text-blue-600 dark:text-blue-400 font-bold self-center">{fmt((r.balance_before || 0) - (r.frozen_amount || 0))}</div>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-9 gap-2 px-3 py-2.5 bg-gray-50 dark:bg-slate-700/50 border-t-2 border-gray-200 dark:border-slate-600 text-xs font-bold">
                  <div className="col-span-3 text-gray-500 dark:text-slate-400">รวมทั้งหมด</div>
                  <div className="col-span-2 text-right text-gray-700 dark:text-slate-200">{fmt(freezeResellerPreview.reduce((s, r) => s + Number(r.balance_before || 0), 0))}</div>
                  <div className="col-span-2 text-right text-orange-500 dark:text-orange-400">-{fmt(freezeResellerPreviewTotal)}</div>
                  <div className="col-span-2 text-right text-blue-600 dark:text-blue-400">{fmt(freezeResellerPreview.reduce((s, r) => s + Number(r.balance_before || 0) - Number(r.frozen_amount || 0), 0))}</div>
                </div>
              </div>
            )}
          </>
        )}
      </UiModal>

      <UiModal
        open={showFreezeConfirmModal && !!previewEvent}
        onClose={() => !creating && setShowFreezeConfirmModal(false)}
        title="ยืนยันการ Freeze"
        icon={faSnowflake}
        iconColor="text-blue-500"
        size="lg"
        scrollable
        footer={
          previewEvent ? (
            <div className="flex gap-3">
              <UiButton variant="secondary" onClick={() => setShowFreezeConfirmModal(false)} disabled={creating} className="flex-1 justify-center">
                ยกเลิก
              </UiButton>
              <UiButton
                variant="danger"
                icon={faSnowflake}
                onClick={() => { setShowFreezeConfirmModal(false); handleCreate(); }}
                disabled={creating}
                loading={creating}
                className="flex-1 justify-center"
              >
                {creating ? 'กำลัง Freeze...' : 'ยืนยัน Freeze'}
              </UiButton>
            </div>
          ) : undefined
        }
      >
        {previewEvent && (
          <>
            <div className="flex items-start gap-3 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
              <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mt-0.5 shrink-0" />
              <div className="text-sm text-red-700 dark:text-red-400">
                <p className="font-semibold mb-1">การดำเนินการนี้ไม่สามารถยกเลิกได้</p>
                <p>
                  {previewEvent.bank
                    ? `ระบบจะหักเงินเฉพาะ Merchant ที่เคยฝากเข้าบัญชี "${bankAccountLabel(previewEvent.bank)}" ตามสัดส่วนยอดคงเหลือในกระเป๋า และหัก Reseller ตามส่วนต่างค่าธรรมเนียม (spread) ของร้านที่ถูก Freeze`
                    : freezeType === 'single' && selectedMerchant
                      ? `ระบบจะหักเงินจาก Merchant "${selectedMerchant.name}" ตามสัดส่วน Pay-in / Pay-out และหักจาก Reseller ของร้านนี้ตามยอด Commission ที่ได้รับจากร้าน (ไม่เกินยอดคงเหลือ)`
                      : 'ระบบจะหักเงินจากทุก Merchant active ตามสัดส่วนที่คำนวณได้ และหักเพิ่ม % เดียวกันจากยอดคงเหลือของ Reseller ทุกราย'}
                </p>
              </div>
            </div>

            {previewEvent.bank && (
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-3 text-sm text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800 space-y-0.5">
                <p>
                  <span className="font-medium">บัญชีฝาก: </span>{bankAccountLabel(previewEvent.bank)}
                  {!previewEvent.bank.is_active && <span className="text-xs text-gray-500 dark:text-slate-400"> (ปิดใช้งาน)</span>}
                </p>
                <p><span className="font-medium">ช่วงวันที่ฝาก: </span>{depositRangeText(previewEvent.deposit_from, previewEvent.deposit_to)}</p>
              </div>
            )}

            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl p-3 text-center border border-red-100 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
                <p className="text-xs text-gray-400 dark:text-slate-500 mb-1">ยอด Freeze</p>
                <p className="text-xl font-bold text-red-600 dark:text-red-400">{fmt(parseFloat(previewScope?.freeze_amount ?? freezeAmount) || 0)}</p>
              </div>
              <div className="rounded-xl p-3 text-center bg-gray-50 dark:bg-slate-700/50">
                <p className="text-xs text-gray-400 dark:text-slate-500 mb-1">หัก %</p>
                <p className="text-xl font-bold text-gray-800 dark:text-slate-100">{Number(previewEvent.freeze_percentage).toFixed(4)}%</p>
              </div>
              <div className="rounded-xl p-3 text-center bg-gray-50 dark:bg-slate-700/50">
                <p className="text-xs text-gray-400 dark:text-slate-500 mb-1">Merchants</p>
                <p className="text-xl font-bold text-gray-800 dark:text-slate-100">{previewEvent.merchant_count} ราย</p>
                {previewEvent.bank && (
                  <p className="text-[10px] text-gray-500 dark:text-slate-400 mt-1">
                    เคยฝาก {previewEvent.deposit_merchant_count ?? 0} ร้าน · ถูกหัก {previewEvent.merchant_count} ร้าน
                  </p>
                )}
              </div>
            </div>

            {previewRecords.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                  รายละเอียดที่จะหักแต่ละ Merchant ({previewRecords.length} ราย)
                </p>
                <div className="rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                  <div className="grid grid-cols-10 gap-2 px-3 py-2 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-100 dark:border-slate-700 text-xs font-semibold text-gray-500 dark:text-slate-400">
                    <div className="col-span-3">Merchant</div>
                    <div className="col-span-2 text-right">Pay-in</div>
                    <div className="col-span-2 text-right">Pay-out</div>
                    <div className="col-span-1 text-right text-orange-500 dark:text-orange-400">หักออก</div>
                    <div className="col-span-2 text-right text-blue-500 dark:text-blue-400">คงเหลือรวม</div>
                  </div>
                  <div className="divide-y divide-gray-50 dark:divide-slate-700 max-h-60 overflow-y-auto">
                    {previewRecords.map((r) => {
                      const totalBefore = parseFloat(((r.payin_balance_before || 0) + (r.payout_balance_before || 0)).toFixed(2));
                      const totalAfter = parseFloat((totalBefore - (r.total_frozen || 0)).toFixed(2));
                      return (
                        <div key={r.id} className="grid grid-cols-10 gap-2 px-3 py-2.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                          <div className="col-span-3">
                            <p className="font-medium text-gray-800 dark:text-slate-100 truncate">{r.merchant_name}</p>
                            <p className="text-gray-400 dark:text-slate-500 truncate text-[10px]">{r.merchant_email}</p>
                          </div>
                          <div className="col-span-2 text-right text-gray-600 dark:text-slate-300 self-center">{fmt(r.payin_balance_before)}</div>
                          <div className="col-span-2 text-right text-gray-600 dark:text-slate-300 self-center">{fmt(r.payout_balance_before)}</div>
                          <div className="col-span-1 text-right text-orange-500 dark:text-orange-400 font-bold self-center">-{fmt(r.total_frozen)}</div>
                          <div className="col-span-2 text-right text-blue-600 dark:text-blue-400 font-bold self-center">{fmt(totalAfter)}</div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="grid grid-cols-10 gap-2 px-3 py-2.5 bg-gray-50 dark:bg-slate-700/50 border-t-2 border-gray-200 dark:border-slate-600 text-xs font-bold">
                    <div className="col-span-3 text-gray-500 dark:text-slate-400">รวมทั้งหมด</div>
                    <div className="col-span-2 text-right text-gray-700 dark:text-slate-200">{fmt(previewRecords.reduce((s, r) => s + (r.payin_balance_before || 0), 0))}</div>
                    <div className="col-span-2 text-right text-gray-700 dark:text-slate-200">{fmt(previewRecords.reduce((s, r) => s + (r.payout_balance_before || 0), 0))}</div>
                    <div className="col-span-1 text-right text-orange-500 dark:text-orange-400">-{fmt(previewEvent.total_frozen)}</div>
                    <div className="col-span-2 text-right text-blue-600 dark:text-blue-400">{fmt(previewRecords.reduce((s, r) => s + (r.payin_balance_before || 0) + (r.payout_balance_before || 0) - (r.total_frozen || 0), 0))}</div>
                  </div>
                </div>
              </div>
            )}

            {previewResellerRecords.length === 0 && (
              <div className="flex gap-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl border border-gray-100 dark:border-slate-700 text-sm text-gray-500 dark:text-slate-400">
                <FontAwesomeIcon icon={faUserTie} className="mt-0.5 shrink-0 text-gray-400 dark:text-slate-500" />
                {previewEvent.bank
                  ? 'จะไม่มีการหัก Reseller — หักตามส่วนต่างค่าธรรมเนียม (spread) ของร้านที่ถูก Freeze แต่ร้านเหล่านี้ไม่มี Reseller หรือยอดที่คำนวณได้เป็น ฿0'
                  : freezeType === 'single'
                    ? 'จะไม่มีการหัก Reseller — ร้านนี้ไม่มี Reseller, Reseller ไม่มี Commission จากร้านนี้ หรือยอดที่คำนวณได้เป็น ฿0'
                    : 'จะไม่มีการหัก Reseller — ไม่มี Reseller ที่มียอดคงเหลือ'}
              </div>
            )}

            {previewResellerRecords.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                  <FontAwesomeIcon icon={faUserTie} className="mr-1.5" />
                  Reseller ที่จะถูกหัก ({previewResellerRecords.length} ราย)
                </p>
                {previewEvent.bank && (
                  <p className="text-xs text-gray-500 dark:text-slate-400 -mt-1 mb-2">
                    หักตามส่วนต่างค่าธรรมเนียม (spread) ของร้านที่ถูก Freeze
                  </p>
                )}
                <div className="rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                  <div className="grid grid-cols-9 gap-2 px-3 py-2 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-100 dark:border-slate-700 text-xs font-semibold text-gray-500 dark:text-slate-400">
                    <div className="col-span-3">Reseller</div>
                    <div className="col-span-2 text-right">Balance ปัจจุบัน</div>
                    <div className="col-span-2 text-right text-orange-500 dark:text-orange-400">หักออก</div>
                    <div className="col-span-2 text-right text-blue-500 dark:text-blue-400">คงเหลือ</div>
                  </div>
                  <div className="divide-y divide-gray-50 dark:divide-slate-700 max-h-40 overflow-y-auto">
                    {previewResellerRecords.map((r) => (
                      <div key={r.id} className="grid grid-cols-9 gap-2 px-3 py-2.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                        <div className="col-span-3">
                          <p className="font-medium text-gray-800 dark:text-slate-100 truncate">{r.reseller_name}</p>
                          <p className="text-gray-400 dark:text-slate-500 truncate text-[10px]">{r.reseller_email}</p>
                        </div>
                        <div className="col-span-2 text-right text-gray-600 dark:text-slate-300 self-center">{fmt(r.balance_before)}</div>
                        <div className="col-span-2 text-right text-orange-500 dark:text-orange-400 font-bold self-center">-{fmt(r.frozen_amount)}</div>
                        <div className="col-span-2 text-right text-blue-600 dark:text-blue-400 font-bold self-center">{fmt((r.balance_before || 0) - (r.frozen_amount || 0))}</div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-9 gap-2 px-3 py-2.5 bg-gray-50 dark:bg-slate-700/50 border-t-2 border-gray-200 dark:border-slate-600 text-xs font-bold">
                    <div className="col-span-3 text-gray-500 dark:text-slate-400">รวมทั้งหมด</div>
                    <div className="col-span-2 text-right text-gray-700 dark:text-slate-200">{fmt(previewResellerRecords.reduce((s, r) => s + Number(r.balance_before || 0), 0))}</div>
                    <div className="col-span-2 text-right text-orange-500 dark:text-orange-400">-{fmt(previewResellerRecords.reduce((s, r) => s + Number(r.frozen_amount || 0), 0))}</div>
                    <div className="col-span-2 text-right text-blue-600 dark:text-blue-400">{fmt(previewResellerRecords.reduce((s, r) => s + Number(r.balance_before || 0) - Number(r.frozen_amount || 0), 0))}</div>
                  </div>
                </div>
              </div>
            )}

            {freezeNotes && (
              <div className="text-sm text-gray-600 dark:text-slate-300 bg-gray-50 dark:bg-slate-700/50 rounded-xl px-4 py-3">
                <span className="font-medium text-gray-700 dark:text-slate-200">หมายเหตุ:</span> {freezeNotes}
              </div>
            )}
          </>
        )}
      </UiModal>

      <UiModal
        open={showUnfreezeModal && !!selectedEvent}
        onClose={closeUnfreezeModal}
        title="ปลด Freeze"
        icon={faLockOpen}
        iconColor="text-green-500"
        size="lg"
        scrollable={unfreezeStep === 2}
        footer={
          selectedEvent ? (
            unfreezeStep === 1 ? (
              <div className="flex gap-3">
                <UiButton variant="secondary" onClick={closeUnfreezeModal} className="flex-1 justify-center">
                  ยกเลิก
                </UiButton>
                <UiButton
                  onClick={handleUnfreezeStep1}
                  disabled={feeAmount === ''}
                  className="flex-1 justify-center !bg-green-600 hover:!bg-green-700"
                >
                  ดำเนินการต่อ →
                </UiButton>
              </div>
            ) : (
              <div className="flex gap-3">
                <UiButton variant="secondary" onClick={() => setUnfreezeStep(1)} disabled={unfreezing} className="flex-1 justify-center">
                  ← กลับ
                </UiButton>
                <UiButton
                  icon={faLockOpen}
                  onClick={handleUnfreeze}
                  disabled={unfreezing}
                  loading={unfreezing}
                  className="flex-1 justify-center !bg-green-600 hover:!bg-green-700"
                >
                  {unfreezing ? 'กำลัง Unfreeze...' : 'ยืนยัน Unfreeze'}
                </UiButton>
              </div>
            )
          ) : undefined
        }
      >
        {selectedEvent && (
          <>
            <div className="flex items-center gap-1.5 text-xs -mt-2 mb-2">
              <span className="w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs bg-green-500 text-white">1</span>
              <div className="w-5 h-px bg-gray-200 dark:bg-slate-600" />
              <span className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-xs ${unfreezeStep === 2 ? 'bg-green-500 text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-400 dark:text-slate-500'}`}>2</span>
            </div>

            {unfreezeStep === 1 && (
              <>
                <div>
                  <label className={LABEL_CLASS}>วิธีคืนเงินให้ Merchant</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <label className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer transition-colors ${
                      isMainWallet
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                    }`}>
                      <input
                        type="radio"
                        name="return_method"
                        checked={isMainWallet}
                        onChange={() => setReturnMethod('main_wallet')}
                        className="mt-0.5 accent-green-600"
                      />
                      <span className="text-sm">
                        <span className="font-semibold text-gray-800 dark:text-slate-100 block">คืนเข้า Main Wallet</span>
                        <span className="text-xs text-gray-500 dark:text-slate-400">เงินบาทเต็มจำนวน — ไม่หักค่าธรรมเนียม ไม่สร้าง Settlement</span>
                      </span>
                    </label>
                    <label className={`flex items-start gap-2.5 rounded-xl border p-3 cursor-pointer transition-colors ${
                      !isMainWallet
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                        : 'border-gray-200 dark:border-slate-600 hover:border-gray-300 dark:hover:border-slate-500'
                    }`}>
                      <input
                        type="radio"
                        name="return_method"
                        checked={!isMainWallet}
                        onChange={() => { setReturnMethod('usdt_settlement'); loadUsdtRate(); }}
                        className="mt-0.5 accent-green-600"
                      />
                      <span className="text-sm">
                        <span className="font-semibold text-gray-800 dark:text-slate-100 block">สร้าง USDT Settlement</span>
                        <span className="text-xs text-gray-500 dark:text-slate-400">แปลงเป็น USDT — หักค่าธรรมเนียม {rateNum}% ตามตั้งค่า</span>
                      </span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className={LABEL_CLASS}>ค่าดำเนินการ (บาท)</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={feeAmount}
                      onChange={(e) => setFeeAmount(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleUnfreezeStep1()}
                      placeholder="เช่น 3000000"
                      className={INPUT_CLASS}
                      autoFocus
                    />
                    <span className="text-gray-500 dark:text-slate-400 font-semibold">฿</span>
                  </div>
                </div>

                {feeAmount !== '' && !isNaN(feeNum) && (
                  <>
                    {!isMainWallet && (
                      <div className="bg-green-50 dark:bg-green-900/20 rounded-xl px-4 py-3 border border-green-100 dark:border-green-800 flex items-center justify-between text-sm">
                        <span className="text-green-700 dark:text-green-400 font-medium">USDT Rate (Bitkub)</span>
                        {usdtRateLoading
                          ? <span className="text-gray-400 dark:text-slate-500 text-xs">กำลังโหลด...</span>
                          : usdtRate
                            ? <span className="font-bold text-green-700 dark:text-green-400">1 USDT = ฿{usdtRate.toLocaleString('th-TH', { minimumFractionDigits: 2 })}</span>
                            : <span className="text-red-400 text-xs">โหลด rate ไม่ได้</span>
                        }
                      </div>
                    )}
                    <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2 text-sm border border-gray-100 dark:border-slate-700">
                      <p className="font-semibold text-gray-700 dark:text-slate-200 text-xs uppercase tracking-wide">ตัวอย่างการคำนวณ</p>
                      <div className="flex justify-between text-gray-500 dark:text-slate-400">
                        <span>ยอด Freeze รวม</span>
                        <span className="font-medium text-gray-800 dark:text-slate-100">{fmt(selectedEvent.total_frozen)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-orange-500 dark:text-orange-400">ค่าดำเนินการ ({feePct.toFixed(4)}%)</span>
                        <span className="font-bold text-orange-500 dark:text-orange-400">{fmt(previewDeduct)}</span>
                      </div>
                      <div className="flex justify-between border-t border-gray-200 dark:border-slate-600 pt-2">
                        <span className="text-green-600 dark:text-green-400">คืนให้ Merchant ({(100 - feePct).toFixed(4)}%)</span>
                        <span className="font-bold text-green-600 dark:text-green-400">{fmt(previewReturn)}</span>
                      </div>
                      {isMainWallet && (
                        <div className="flex justify-between">
                          <span className="text-green-700 dark:text-green-400 font-medium">เข้า Main Wallet เต็มจำนวน (ไม่หักค่าธรรมเนียม)</span>
                          <span className="font-bold text-green-700 dark:text-green-400">{fmt(previewNet)}</span>
                        </div>
                      )}
                      {totalResellerFrozen > 0 && (
                        <div className="border-t border-gray-200 dark:border-slate-600 pt-2 space-y-1.5">
                          <div className="flex justify-between text-gray-500 dark:text-slate-400">
                            <span>ยอด Freeze Reseller รวม ({resellerRecords.length} ราย)</span>
                            <span className="font-medium text-gray-800 dark:text-slate-100">{fmt(totalResellerFrozen)}</span>
                          </div>
                          <div className="flex justify-between text-orange-500 dark:text-orange-400">
                            <span>ค่าดำเนินการ Reseller ({feePct.toFixed(4)}%)</span>
                            <span className="font-medium">-{fmt(resellerDeductPreview)}</span>
                          </div>
                          <div className="flex justify-between text-purple-600 dark:text-purple-400 font-medium">
                            <span>คืนเข้ากระเป๋า Reseller โดยตรง (ไม่หักค่าธรรมเนียม)</span>
                            <span className="font-bold">{fmt(resellerReturnPreview)}</span>
                          </div>
                        </div>
                      )}
                      {!isMainWallet && previewReturn > 0 && (
                        <>
                          <div className="flex justify-between border-t border-gray-200 dark:border-slate-600 pt-2">
                            <span className="text-red-500 dark:text-red-400">ค่าธรรมเนียม Settlement {rateNum}%</span>
                            <span className="font-medium text-red-500 dark:text-red-400">-{fmt(previewSettleFee)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-green-600 dark:text-green-400">ยอดสุทธิ (THB)</span>
                            <span className="font-semibold text-green-600 dark:text-green-400">{fmt(previewNet)}</span>
                          </div>
                          {usdtRate && (
                            <div className="flex justify-between">
                              <span className="text-blue-600 dark:text-blue-400">≈ USDT</span>
                              <span className="font-bold text-blue-600 dark:text-blue-400">≈ {(previewNet / usdtRate).toFixed(2)} USDT</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </>
                )}
              </>
            )}

            {unfreezeStep === 2 && (
              <>
                <div className="flex gap-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-100 dark:border-orange-800">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="text-orange-500 mt-0.5 shrink-0" />
                  <p className="text-sm text-orange-700 dark:text-orange-300">
                    ยืนยันการปลด Freeze? ระบบจะ{isMainWallet
                      ? <>คืนเงินบาทเต็มจำนวน <strong>เข้า Main Wallet</strong> ของทุก Merchant (ไม่หักค่าธรรมเนียม ไม่สร้าง Settlement)</>
                      : <>สร้าง <strong>USDT Settlement</strong> ให้ทุก Merchant อัตโนมัติ (หักค่าธรรมเนียม {rateNum}%)</>}
                    {totalResellerFrozen > 0 && (
                      <> และคืนเงิน Reseller {resellerRecords.length} ราย <strong>เข้ากระเป๋าโดยตรง</strong> (ไม่หักค่าธรรมเนียม)</>
                    )}
                    {' '}— การกระทำนี้ไม่สามารถยกเลิกได้
                  </p>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-3 border border-green-100 dark:border-green-800 text-sm space-y-1.5">
                  <p className="font-semibold text-green-700 dark:text-green-400 text-xs uppercase tracking-wide">
                    {isMainWallet ? 'ยอดคืนเข้า Main Wallet' : 'USDT Settlement ที่จะสร้าง'}
                  </p>
                  <div className="flex justify-between text-gray-600 dark:text-slate-300">
                    <span>จำนวน Merchant</span>
                    <span className="font-bold text-green-700 dark:text-green-400">{records.filter(r => (r.total_frozen || 0) > 0).length} ราย</span>
                  </div>
                  <div className="flex justify-between text-gray-600 dark:text-slate-300">
                    <span>ยอดคืนรวม (THB)</span>
                    <span className="font-medium text-gray-800 dark:text-slate-100">{fmt(previewReturn)}</span>
                  </div>
                  {!isMainWallet && (
                    <div className="flex justify-between text-red-500 dark:text-red-400">
                      <span>ค่าธรรมเนียม Settlement {rateNum}%</span>
                      <span className="font-medium">-{fmt(previewSettleFee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-green-200 dark:border-green-800 pt-1.5 text-green-700 dark:text-green-400 font-semibold">
                    <span>{isMainWallet ? 'ยอดที่เข้ากระเป๋า (THB)' : 'ยอดสุทธิ (THB) ที่ merchant ได้'}</span>
                    <span>{fmt(previewNet)}</span>
                  </div>
                  {!isMainWallet && usdtRate && (
                    <>
                      <div className="flex justify-between text-gray-500 dark:text-slate-400 text-xs pt-0.5">
                        <span>USDT Rate</span>
                        <span>฿{usdtRate.toLocaleString('th-TH', { minimumFractionDigits: 2 })} / USDT</span>
                      </div>
                      <div className="flex justify-between text-blue-600 dark:text-blue-400 font-semibold">
                        <span>≈ USDT รวม</span>
                        <span>≈ {(previewNet / usdtRate).toFixed(2)} USDT</span>
                      </div>
                    </>
                  )}
                </div>

                <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2 text-sm">
                  <p className="font-semibold text-gray-700 dark:text-slate-200 text-xs uppercase tracking-wide mb-2">สรุปการ Unfreeze</p>
                  <div className="flex justify-between text-gray-500 dark:text-slate-400">
                    <span>ยอด Freeze รวม</span>
                    <span className="font-medium text-gray-800 dark:text-slate-100">{fmt(selectedEvent.total_frozen)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-orange-500 dark:text-orange-400">ค่าดำเนินการ ({feePct.toFixed(4)}%)</span>
                    <span className="font-bold text-orange-500 dark:text-orange-400">{fmt(previewDeduct)}</span>
                  </div>
                  <div className="flex justify-between border-t border-gray-200 dark:border-slate-600 pt-2">
                    <span className="text-green-700 dark:text-green-400">คืนให้ Merchant รวม ({(100 - feePct).toFixed(4)}%)</span>
                    <span className="font-semibold text-green-600 dark:text-green-400">{fmt(previewReturn)}</span>
                  </div>
                  {!isMainWallet && (
                    <div className="flex justify-between">
                      <span className="text-red-500 dark:text-red-400">ค่าธรรมเนียม Settlement {rateNum}%</span>
                      <span className="font-medium text-red-500 dark:text-red-400">-{fmt(previewSettleFee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-gray-200 dark:border-slate-600 pt-2">
                    <span className="font-bold text-green-700 dark:text-green-400">
                      {isMainWallet ? 'ยอดที่เข้า Main Wallet' : 'ยอดสุทธิที่ Merchant ได้รับ'}
                    </span>
                    <span className="font-bold text-green-600 dark:text-green-400 text-base">{fmt(previewNet)}</span>
                  </div>
                </div>

                {records.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                      รายละเอียดแต่ละ Merchant ({records.length} ราย)
                    </p>
                    <div className="rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                      <div className="grid grid-cols-12 gap-1 px-3 py-2 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-100 dark:border-slate-700 text-xs font-semibold text-gray-500 dark:text-slate-400">
                        <div className="col-span-3">Merchant</div>
                        <div className="col-span-2 text-right">Pay-in frozen</div>
                        <div className="col-span-2 text-right">Pay-out frozen</div>
                        {isMainWallet ? (
                          <>
                            <div className="col-span-2 text-right text-orange-500 dark:text-orange-400">หัก</div>
                            <div className="col-span-3 text-right text-green-600 dark:text-green-400">คืนเข้า Main Wallet</div>
                          </>
                        ) : (
                          <>
                            <div className="col-span-1 text-right text-orange-500 dark:text-orange-400">หัก</div>
                            <div className="col-span-2 text-right text-green-600 dark:text-green-400">คืน (หัก {rateNum}%)</div>
                            <div className="col-span-2 text-right text-blue-600 dark:text-blue-400">≈ USDT</div>
                          </>
                        )}
                      </div>
                      <div className="divide-y divide-gray-50 dark:divide-slate-700 max-h-56 overflow-y-auto">
                        {records.map((r) => {
                          const frozen = r.total_frozen || 0;
                          const feeDeducted = parseFloat((frozen * feePct / 100).toFixed(2));
                          const returned = parseFloat((frozen - feeDeducted).toFixed(2));
                          const netThb = isMainWallet ? returned : parseFloat((returned - parseFloat((returned * rateNum / 100).toFixed(2))).toFixed(2));
                          const usdtAmt = !isMainWallet && usdtRate && netThb > 0 ? (netThb / usdtRate).toFixed(2) : null;
                          return (
                            <div key={r.id} className="grid grid-cols-12 gap-1 px-3 py-2.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                              <div className="col-span-3">
                                <p className="font-medium text-gray-800 dark:text-slate-100 truncate">{r.merchant_name}</p>
                                <p className="text-gray-400 dark:text-slate-500 truncate text-[10px]">{r.merchant_email}</p>
                              </div>
                              <div className="col-span-2 text-right text-gray-600 dark:text-slate-300 self-center">{fmt(r.payin_amount)}</div>
                              <div className="col-span-2 text-right text-gray-600 dark:text-slate-300 self-center">{fmt(r.payout_amount)}</div>
                              {isMainWallet ? (
                                <>
                                  <div className="col-span-2 text-right text-orange-500 dark:text-orange-400 font-medium self-center">-{fmt(feeDeducted)}</div>
                                  <div className="col-span-3 text-right text-green-600 dark:text-green-400 font-bold self-center">{fmt(netThb)}</div>
                                </>
                              ) : (
                                <>
                                  <div className="col-span-1 text-right text-orange-500 dark:text-orange-400 font-medium self-center">-{fmt(feeDeducted)}</div>
                                  <div className="col-span-2 text-right text-green-600 dark:text-green-400 font-bold self-center">{fmt(netThb)}</div>
                                  <div className="col-span-2 text-right text-blue-600 dark:text-blue-400 font-semibold self-center">
                                    {usdtAmt ? `≈${usdtAmt}` : '-'}
                                  </div>
                                </>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className={`mt-2 grid gap-2 text-sm ${isMainWallet ? 'grid-cols-2' : 'grid-cols-3'}`}>
                      <div className="rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 px-3 py-2.5 text-center">
                        <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-1">ค่าดำเนินการรวม</p>
                        <p className="font-bold text-orange-500 dark:text-orange-400">-{fmt(previewDeduct)}</p>
                      </div>
                      <div className="rounded-xl bg-green-50 dark:bg-green-900/20 border border-green-100 dark:border-green-800 px-3 py-2.5 text-center">
                        <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-1">
                          {isMainWallet ? 'คืนเข้า Main Wallet รวม' : `คืนสุทธิรวม (หัก ${rateNum}%)`}
                        </p>
                        <p className="font-bold text-green-600 dark:text-green-400">{fmt(previewNet)}</p>
                      </div>
                      {!isMainWallet && (
                        <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 px-3 py-2.5 text-center">
                          <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-1">≈ USDT รวม</p>
                          <p className="font-bold text-blue-600 dark:text-blue-400">
                            {usdtRate ? `≈ ${(previewNet / usdtRate).toFixed(2)} USDT` : '-'}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {totalResellerFrozen > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                      <FontAwesomeIcon icon={faUserTie} className="mr-1.5" />
                      Reseller ({resellerRecords.length} ราย)
                    </p>
                    <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl px-4 py-3 border border-purple-100 dark:border-purple-800 text-sm text-purple-700 dark:text-purple-300 mb-2">
                      คืนเข้ากระเป๋าโดยตรง — หักค่าดำเนินการ {feePct.toFixed(4)}% เท่ากับ Merchant, ไม่หักค่าธรรมเนียม Settlement
                    </div>
                    <div className="rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                      <div className="grid grid-cols-9 gap-1 px-3 py-2 bg-gray-50 dark:bg-slate-700/50 border-b border-gray-100 dark:border-slate-700 text-xs font-semibold text-gray-500 dark:text-slate-400">
                        <div className="col-span-3">Reseller</div>
                        <div className="col-span-2 text-right">ยอดที่ถูก Freeze</div>
                        <div className="col-span-2 text-right text-orange-500 dark:text-orange-400">หัก ({feePct.toFixed(2)}%)</div>
                        <div className="col-span-2 text-right text-purple-600 dark:text-purple-400">คืนเข้ากระเป๋า</div>
                      </div>
                      <div className="divide-y divide-gray-50 dark:divide-slate-700 max-h-40 overflow-y-auto">
                        {resellerRecords.map((r) => {
                          const frozen = Number(r.frozen_amount) || 0;
                          const deducted = parseFloat((frozen * feePct / 100).toFixed(2));
                          const returned = parseFloat((frozen - deducted).toFixed(2));
                          return (
                            <div key={r.id} className="grid grid-cols-9 gap-1 px-3 py-2.5 text-xs hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                              <div className="col-span-3">
                                <p className="font-medium text-gray-800 dark:text-slate-100 truncate">{r.reseller_name}</p>
                                <p className="text-gray-400 dark:text-slate-500 truncate text-[10px]">{r.reseller_email}</p>
                              </div>
                              <div className="col-span-2 text-right text-gray-600 dark:text-slate-300 self-center">{fmt(frozen)}</div>
                              <div className="col-span-2 text-right text-orange-500 dark:text-orange-400 font-medium self-center">-{fmt(deducted)}</div>
                              <div className="col-span-2 text-right text-purple-600 dark:text-purple-400 font-bold self-center">{fmt(returned)}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                      <div className="rounded-xl bg-orange-50 dark:bg-orange-900/20 border border-orange-100 dark:border-orange-800 px-3 py-2.5 text-center">
                        <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-1">ค่าดำเนินการ Reseller รวม</p>
                        <p className="font-bold text-orange-500 dark:text-orange-400">-{fmt(resellerDeductPreview)}</p>
                      </div>
                      <div className="rounded-xl bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 px-3 py-2.5 text-center">
                        <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-1">คืนเข้ากระเป๋า Reseller รวม</p>
                        <p className="font-bold text-purple-600 dark:text-purple-400">{fmt(resellerReturnPreview)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </UiModal>

      <UiModal
        open={showCreateModal}
        onClose={closeCreateModal}
        title="สร้าง Freeze"
        icon={faSnowflake}
        iconStyle={{ color: 'var(--theme-primary)' }}
        size="md"
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={closeCreateModal} className="flex-1 justify-center">
              ยกเลิก
            </UiButton>
            <UiButton
              variant="danger"
              icon={faSnowflake}
              onClick={async () => {
                const amount = parseFloat(freezeAmount);
                if (isNaN(amount) || amount <= 0) return;
                if (freezeType === 'single' && !selectedMerchant) { toast.error('กรุณาเลือก Merchant'); return; }
                if (bankFilterActive && depositFrom && depositTo && dayjs(depositFrom).isAfter(dayjs(depositTo), 'day')) {
                  toast.error('วันที่ฝากเริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด');
                  return;
                }
                try {
                  setPreviewLoading(true);
                  const scope: Record<string, string> = {
                    freeze_amount: String(amount),
                    ...(freezeType === 'single' && selectedMerchant ? { merchant_id: selectedMerchant.id } : {}),
                    ...bankFilterParams(),
                  };
                  const qs = new URLSearchParams(scope);
                  const res = await merchantApi.get(`/admin/freeze/preview?${qs}`);
                  setPreviewEvent(res.data.data.event);
                  setPreviewRecords(res.data.data.records || []);
                  setPreviewResellerRecords(res.data.data.reseller_records || []);
                  setPreviewScope(scope);
                  setShowFreezeConfirmModal(true);
                } catch (err: any) {
                  toast.error(err.response?.data?.message || 'ไม่สามารถโหลด Preview ได้');
                } finally {
                  setPreviewLoading(false);
                }
              }}
              disabled={!freezeAmount || parseFloat(freezeAmount) <= 0 || previewLoading || (freezeType === 'single' && !selectedMerchant)}
              loading={previewLoading}
              className="flex-1 justify-center"
            >
              {previewLoading ? 'กำลังคำนวณ...' : 'Freeze'}
            </UiButton>
          </div>
        }
      >
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setFreezeType('all')}
            className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors flex items-center justify-center gap-1.5 ${
              freezeType === 'all'
                ? 'bg-red-600 text-white border-red-600'
                : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300 border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600'
            }`}
          >
            <FontAwesomeIcon icon={faUsers} />
            ทั้งหมด
          </button>
          <button
            type="button"
            onClick={() => { setFreezeType('single'); loadMerchants(); }}
            className={`flex-1 py-2.5 rounded-xl text-sm font-medium border transition-colors flex items-center justify-center gap-1.5 ${
              freezeType === 'single'
                ? 'bg-red-600 text-white border-red-600'
                : 'bg-white dark:bg-slate-700 text-gray-600 dark:text-slate-300 border-gray-200 dark:border-slate-600 hover:bg-gray-50 dark:hover:bg-slate-600'
            }`}
          >
            <FontAwesomeIcon icon={faUser} />
            เฉพาะร้านค้า
          </button>
        </div>

        <div className="flex gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800 text-sm text-blue-700 dark:text-blue-300">
          {freezeType === 'all'
            ? bankFilterActive
              ? `หักเฉพาะ Merchant ที่เคยฝากเข้าบัญชี${selectedDepositBank ? ` ${bankAccountLabel(selectedDepositBank)}` : 'ที่เลือก'} — ระบบจะคำนวณ % จากยอด Pay-in + Pay-out รวมของร้านกลุ่มนี้ แล้วหักตามสัดส่วนยอดคงเหลือเหมือนเดิม`
              : 'ระบบจะคำนวณ % จากยอด Pay-in + Pay-out รวมของทุก Merchant active แล้วหักตามสัดส่วน'
            : 'หักยอดเงินตามสัดส่วน Pay-in / Pay-out จาก Merchant ที่เลือกเท่านั้น'
          }
        </div>

        {freezeType === 'all' && bankFilterSupported && (
          <div className="space-y-3">
            <div>
              <label className={LABEL_CLASS}>บัญชีฝาก (ไม่บังคับ)</label>
              <select
                value={depositBankId}
                onChange={(e) => handleDepositBankChange(e.target.value)}
                className={INPUT_CLASS}
              >
                <option value="">— ไม่ระบุ (หักทุก Merchant) —</option>
                {depositBanks.map(b => (
                  <option key={b.id} value={b.id}>
                    {bankAccountLabel(b)}{!b.is_active ? ' (ปิดใช้งาน)' : ''}
                  </option>
                ))}
              </select>
            </div>
            {depositBankId && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL_CLASS}>ฝากตั้งแต่วันที่ (ไม่บังคับ)</label>
                    <input
                      type="date"
                      value={depositFrom}
                      onChange={(e) => setDepositFrom(e.target.value)}
                      max={depositTo || undefined}
                      className={INPUT_CLASS}
                    />
                  </div>
                  <div>
                    <label className={LABEL_CLASS}>ถึงวันที่ (ไม่บังคับ)</label>
                    <input
                      type="date"
                      value={depositTo}
                      onChange={(e) => setDepositTo(e.target.value)}
                      min={depositFrom || undefined}
                      className={INPUT_CLASS}
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500 dark:text-slate-400">
                  นับเฉพาะรายการฝาก/เติม Payout ที่ระบบกำหนดให้โอนเข้าบัญชีนี้และชำระแล้ว
                </p>
              </>
            )}
          </div>
        )}

        {freezeType === 'single' && (
          <div className="relative">
            <label className={LABEL_CLASS}>
              เลือก Merchant <span className="text-red-500">*</span>
            </label>
            {selectedMerchant ? (
              <div className="flex items-center justify-between px-3 py-2.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-sm">
                <div>
                  <span className="font-medium text-blue-800 dark:text-blue-300">{selectedMerchant.name}</span>
                  <span className="text-blue-500 dark:text-blue-400 text-xs ml-2">{selectedMerchant.email}</span>
                </div>
                <button
                  type="button"
                  onClick={() => { setSelectedMerchant(null); setMerchantSearch(''); }}
                  className="text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 ml-2"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            ) : (
              <input
                type="text"
                value={merchantSearch}
                onChange={(e) => { setMerchantSearch(e.target.value); setShowMerchantDropdown(true); }}
                onFocus={() => setShowMerchantDropdown(true)}
                onBlur={() => setTimeout(() => setShowMerchantDropdown(false), 150)}
                placeholder="ค้นหาชื่อหรืออีเมล Merchant..."
                className={INPUT_CLASS}
                autoFocus
              />
            )}
            {showMerchantDropdown && !selectedMerchant && merchantSearch && (
              <div className="absolute z-20 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                {merchantOptionsLoading ? (
                  <div className="px-3 py-2.5 text-sm text-gray-400 dark:text-slate-500">กำลังโหลด...</div>
                ) : filteredMerchants.length === 0 ? (
                  <div className="px-3 py-2.5 text-sm text-gray-400 dark:text-slate-500">ไม่พบ Merchant</div>
                ) : filteredMerchants.slice(0, 10).map(m => (
                  <button
                    type="button"
                    key={m.id}
                    onMouseDown={() => { setSelectedMerchant(m); setMerchantSearch(m.name); setShowMerchantDropdown(false); }}
                    className="w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors border-b border-gray-50 dark:border-slate-700 last:border-0"
                  >
                    <p className="text-sm font-medium text-gray-800 dark:text-slate-100">{m.name}</p>
                    <p className="text-xs text-gray-400 dark:text-slate-500">{m.email}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div>
          <label className={LABEL_CLASS}>จำนวนเงินที่ต้องการ Freeze (บาท) <span className="text-red-500">*</span></label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={freezeAmount}
            onChange={(e) => setFreezeAmount(e.target.value)}
            placeholder="เช่น 8000000"
            className={INPUT_CLASS}
            autoFocus={freezeType === 'all'}
          />
        </div>

        <div>
          <label className={LABEL_CLASS}>วันที่ Freeze</label>
          <input
            type="date"
            value={freezeDate}
            onChange={(e) => setFreezeDate(e.target.value)}
            className={INPUT_CLASS}
          />
        </div>

        <div>
          <label className={LABEL_CLASS}>หมายเหตุ (ไม่บังคับ)</label>
          <textarea
            value={freezeNotes}
            onChange={(e) => setFreezeNotes(e.target.value)}
            rows={2}
            placeholder="เหตุผลที่ Freeze..."
            className={TEXTAREA_CLASS}
          />
        </div>
      </UiModal>
    </div>
  );
};

export default FreezeManagementPage;
